import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Trophy,
  Users,
  FileText,
  TrendingUp,
  Sparkles,
  BarChart3,
  Eye,
  Heart,
  MessageSquare,
  ChevronRight,
  Loader2,
  UserPlus,
  Hash,
  FolderOpen,
  Globe,
  Lock,
  Plus,
  User,
  Settings,
  Share2,
  DollarSign,
} from 'lucide-react';
import { BadgeIcon, isBadgeType } from '@/components/badges';
import Link from '@/components/LocaleLink';
import { useDesignV2 } from '@/hooks/useDesignV2';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface UserStats {
  scoopScore: number;
  followers: number;
  following: number;
  totalPosts: number;
  totalViews: number;
  totalLikes: number;
}

interface TrendingHub {
  id: string;
  name: string;
  memberCount: number;
  postCount24h: number;
  coverImage?: string;
  /** Emoji the hub owner chose. Optional — older hubs may not have one. */
  icon?: string | null;
}

interface SuggestedItem {
  type: 'hub' | 'user';
  id: string;
  name: string;
  username?: string;
  avatar?: string;
  description?: string;
  memberCount?: number;
  followers?: number;
  badge?: string;
}

interface ActivitySummary {
  viewsToday: number;
  viewsChange: number;
  likesToday: number;
  likesChange: number;
  commentsToday: number;
  commentsChange: number;
}

interface UserCollection {
  id: string;
  name: string;
  icon: string;
  visibility: string;
  itemCount: number;
}

interface UserProfile {
  username: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  bio: string | null;
  collectionCount: number;
  hubCount: number;
}

export function RightSidebar() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const designV2 = useDesignV2();
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [trendingHubs, setTrendingHubs] = useState<TrendingHub[]>([]);
  const [suggestions, setSuggestions] = useState<SuggestedItem[]>([]);
  const [activity, setActivity] = useState<ActivitySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [collections, setCollections] = useState<UserCollection[]>([]);
  const [profile, setProfile] = useState<UserProfile | null>(null);

  // Determine context from pathname
  // Workspace context for the V2 home rail: the spaces you belong to and
  // what your week looks like. Every value is read from a real endpoint —
  // a workspace panel that invents its numbers is worse than no panel.
  const [myHubs, setMyHubs] = useState<Array<{
    id: string;
    name?: string;
    title?: string;
    member_count?: number;
    icon?: string | null;
    cover_image_url?: string | null;
  }>>([]);
  const [weekStats, setWeekStats] = useState<{ renders: number; drafts: number; credits: number | null }>({
    renders: 0, drafts: 0, credits: null,
  });

  const isCollectionsPage = pathname?.includes('/collections');
  const isProfilePage = pathname?.includes('/profile') && !pathname?.includes('/profile/');
  const isSettingsPage = pathname?.includes('/settings');
  const isSecurityPage = pathname?.includes('/security');
  const isHubsPage = pathname?.includes('/scoops') && !pathname?.includes('/scoops/');
  const isHubDetailPage = pathname?.includes('/scoops/') && pathname?.split('/').length > 3;
  const isDiscoverPage = pathname?.includes('/discover');
  const isCreatorPage = pathname?.includes('/creator') && !pathname?.includes('/creator-fund');
  const isAnalyticsPage = pathname?.includes('/analytics');
  const isMessagesPage = pathname?.includes('/messages');
  const isNotificationsPage = pathname?.includes('/notifications');
  const isScoopScorePage = pathname?.includes('/scoop-score');
  const isCreatorFundPage = pathname?.includes('/creator-fund');
  const isMyPostsPage = pathname?.includes('/my-posts');
  const isRoomsPage = pathname?.includes('/rooms') && !pathname?.includes('/rooms/');
  const isCreditsPage = pathname?.includes('/credits');
  const isInvitationsPage = pathname?.includes('/invitations');
  const isAIPage = pathname?.includes('/ai');
  const isBookmarksPage = pathname?.includes('/bookmarks');
  const isFeedPage = pathname?.includes('/feed');
  const isExplorePage = pathname?.includes('/explore');
  const isReadingListPage = pathname?.includes('/reading-list');
  // /dashboard/home-next is a home surface too. Without it here the rail
  // still MOUNTED and fetched on that route, then matched no render branch
  // and drew nothing — which is why the page showed a spinner that resolved
  // into empty space, and why Trending / Who to follow were missing there.
  const isHomePage =
    pathname === '/dashboard' ||
    pathname?.endsWith('/dashboard') ||
    pathname?.endsWith('/dashboard/home-next');
  // /dashboard/media + /dashboard/my-library both surface user uploads — rail
  // should show storage usage and quick upload, not Trending Communities.
  const isMediaPage = pathname?.includes('/dashboard/media') || pathname?.includes('/my-library');

  useEffect(() => {
    if (!(designV2 && isHomePage)) return;
    const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
    if (!token) return;
    let alive = true;
    const headers = { Authorization: `Bearer ${token}` };
    fetch(`${API_URL}/collections`, { headers })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (!alive || !d) return;
        // "Your spaces" means every hub you're in — the ones you JOINED and
        // the ones you OWN. The joinedHubs query deliberately excludes your
        // own (`c.user_id != $1`); they come back in `collections` instead.
        // Reading only joinedHubs hid a founder's own hubs from their rail.
        const joined = Array.isArray(d.joinedHubs) ? d.joinedHubs : [];
        const ownedHubs = (Array.isArray(d.collections) ? d.collections : [])
          .filter((c: any) => c.is_community === true);
        const seen = new Set<string>();
        setMyHubs([...ownedHubs, ...joined].filter((h: any) => {
          if (!h?.id || seen.has(h.id)) return false;
          seen.add(h.id);
          return true;
        }));
      })
      .catch(() => {});
    Promise.all([
      fetch(`${API_URL}/ai-studio/projects`, { headers }).then(r => (r.ok ? r.json() : null)).catch(() => null),
      fetch(`${API_URL}/ai-studio/quota`, { headers }).then(r => (r.ok ? r.json() : null)).catch(() => null),
    ]).then(([proj, quota]) => {
      if (!alive) return;
      const list: any[] = Array.isArray(proj?.projects) ? proj.projects : [];
      const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      setWeekStats({
        renders: list.filter(p => p.status === 'completed' && new Date(p.updated_at).getTime() > weekAgo).length,
        drafts: list.filter(p => p.status === 'draft').length,
        credits: typeof quota?.balance === 'number' ? quota.balance : null,
      });
    });
    return () => { alive = false; };
  }, [designV2, isHomePage]);

  useEffect(() => {
    async function fetchData() {
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      const authHeaders: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

      try {
        // Base fetches for all pages
        const fetchPromises: Promise<Response | null>[] = [
          fetch(`${API_URL}/user/profile`, { headers: authHeaders }).catch(() => null),
          fetch(`${API_URL}/discover/trending?limit=5`).catch(() => null),
          fetch(`${API_URL}/onboarding/suggestions/users?limit=4`, { headers: authHeaders }).catch(() => null),
        ];

        // Context-specific fetches
        if (isCollectionsPage || isProfilePage) {
          fetchPromises.push(
            fetch(`${API_URL}/user-collections`, { headers: authHeaders }).catch(() => null)
          );
        }

        // Activity summary placeholder (extension analytics - skip on web)
        if (isHomePage) {
          fetchPromises.push(Promise.resolve(null));
        }

        const responses = await Promise.all(fetchPromises);
        const [profileRes, trendingRes, suggestionsRes, collectionsRes, activityRes] = responses;

        let profileData: any = null;
        if (profileRes?.ok) {
          profileData = await profileRes.json();
          setUserStats({
            scoopScore: profileData.scoop_score || 0,
            followers: profileData.follower_count || 0,
            following: profileData.following_count || 0,
            totalPosts: profileData.scoop_count || 0,
            totalViews: profileData.total_views || 0,
            totalLikes: profileData.total_likes || 0,
          });
          setProfile({
            username: profileData.username,
            displayName: profileData.display_name,
            avatarUrl: profileData.avatar_url,
            bio: profileData.bio,
            collectionCount: profileData.collection_count || 0,
            hubCount: profileData.hub_count || 0,
          });
        } else {
          // Fallback mock data for development
          setUserStats({
            scoopScore: 0,
            followers: 0,
            following: 0,
            totalPosts: 0,
            totalViews: 0,
            totalLikes: 0,
          });
        }

        if (trendingRes?.ok) {
          const data = await trendingRes.json();
          setTrendingHubs((data.hubs || data.scoops || []).slice(0, 5).map((h: any) => ({
            id: h.id || h.hub_id,
            name: h.name || h.title || 'Untitled',
            memberCount: h.member_count || h.follower_count || 0,
            postCount24h: h.post_count_24h || 0,
            icon: h.icon || null,
          })));
        }

        if (suggestionsRes?.ok) {
          const data = await suggestionsRes.json();
          const users = data.users || [];
          setSuggestions(users.slice(0, 4).map((u: any) => ({
            type: 'user' as const,
            id: u.id,
            name: u.display_name || u.username,
            username: u.username,
            avatar: u.avatar_url,
            followers: u.follower_count || 0,
            badge: u.badge_type,
          })));
        }

        // Collections data for collections/profile pages
        if (collectionsRes?.ok) {
          const data = await collectionsRes.json();
          setCollections((data.collections || []).slice(0, 5).map((c: any) => ({
            id: c.id,
            name: c.name,
            icon: c.icon || '📚',
            visibility: c.visibility || 'private',
            itemCount: c.itemCount || 0,
          })));
        }

        // Activity summary from analytics API or profile fallback
        if (activityRes?.ok) {
          const data = await activityRes.json();
          const s = data.summary || data;
          setActivity({
            viewsToday: s.views_today ?? s.total_views ?? 0,
            viewsChange: s.views_change ?? 0,
            likesToday: s.likes_today ?? s.total_likes ?? 0,
            likesChange: s.likes_change ?? 0,
            commentsToday: s.comments_today ?? s.total_comments ?? 0,
            commentsChange: s.comments_change ?? 0,
          });
        } else {
          // Fallback: use profile totals
          setActivity({
            viewsToday: profileData?.total_views ?? 0,
            viewsChange: 0,
            likesToday: profileData?.total_likes ?? 0,
            likesChange: 0,
            commentsToday: 0,
            commentsChange: 0,
          });
        }
      } catch (err) {
        console.error('Failed to fetch sidebar data:', err);
        // Set fallback data
        setActivity({
          viewsToday: 0,
          viewsChange: 0,
          likesToday: 0,
          likesChange: 0,
          commentsToday: 0,
          commentsChange: 0,
        });
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [isCollectionsPage, isProfilePage, isHomePage]);

  if (loading) {
    return (
      <div className="w-80 shrink-0">
        <div className="sticky top-6 space-y-4">
          <div className="bg-[#16161e] rounded-xl p-6 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-brand-primary animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  // V2 — chromeless right rail per docs/design/mocks/dashboard-feed.html.
  // No cards, no badges — the feed is the content; the rail is a thin strip.
  //
  // Order is yours-before-world: Your spaces → This week → Trending → Who to
  // follow. The top of the rail is the only part visible without scrolling,
  // so it holds what a returning user acts on (which space to open, drafts
  // waiting, credits left). For a NEW user both personal sections are empty
  // and collapse away, so discovery floats to the top on its own — the order
  // adapts to who is looking instead of needing two layouts.
  //
  // Home-only: trending + who-to-follow is feed-context content. On other
  // pages (Media, Collections, Profile, Discover…) we fall through to the
  // existing page-specific rails below so users get content relevant to the
  // surface they're on (collections summary, media tips, etc.), not a
  // mismatched discovery strip.
  //
  // `This week` is gated like every other section. Unconditional, it pinned a
  // dead `0 / 0 / —` block near the top of a new user's rail.
  const showWeek = weekStats.renders > 0 || weekStats.drafts > 0 || weekStats.credits !== null;
  if (designV2 && isHomePage) {
    return (
      // The rail pins to the viewport and scrolls internally when it outgrows
      // it. Previously only the search input carried `sticky`, inside a plain
      // (non-sticky) aside — so the whole rail scrolled off with the page and
      // the "sticky" search went with it. Every other rail in this file
      // already does this; the V2 home branch was the one that missed it.
      // `sticky` lives on the <aside> itself, NOT on an inner wrapper. A
      // sticky box can only travel inside its own parent, and an inner div's
      // parent would be this aside — which is exactly as tall as its content,
      // leaving zero room to stick. The aside's parent (the layout's rail
      // column) stretches to the full flex-row height, so the aside has room.
      <aside className="w-80 shrink-0 py-4 sticky top-4 max-h-[calc(100vh-2rem)] overflow-y-auto overscroll-contain">
        <div className="flex flex-col gap-6">

        {myHubs.length > 0 && (
          <section className="flex flex-col">
            <div className="text-sm font-semibold text-white px-2 mb-2">
              {t('rail.yourSpaces', 'Your spaces')}
            </div>
            {myHubs.slice(0, 5).map((h) => (
              <Link
                key={h.id}
                // Hubs open at /dashboard/scoops/:id — the same pattern this
                // file already uses for Trending and suggestions. The
                // /dashboard/collections/:id route is for COLLECTIONS; a hub
                // id 404s there and CollectionDetail bounces to /dashboard.
                to={`/dashboard/scoops/${h.id}`}
                className="px-2 py-2 rounded-lg hover:bg-white/4 transition-colors cursor-pointer flex items-center gap-3"
              >
                {/* Was a bare grey square — the cover and emoji were already
                    on the payload (`/collections` returns cover_image_url +
                    icon), nothing ever read them. Initial letter is the last
                    resort, matching the who-to-follow avatars above. */}
                <span className="w-8 h-8 rounded-lg bg-white/6 shrink-0 flex items-center justify-center overflow-hidden text-sm text-white">
                  {h.cover_image_url
                    ? <img src={h.cover_image_url} alt="" loading="lazy" className="w-full h-full object-cover" />
                    : (h.icon || (h.name || h.title || '?').charAt(0).toUpperCase())}
                </span>
                <span className="flex-1 min-w-0 truncate text-sm text-white">{h.name || h.title}</span>
                {typeof h.member_count === 'number' && (
                  <span className="text-xs tabular-nums text-[#9a9aac]">{h.member_count}</span>
                )}
              </Link>
            ))}
          </section>
        )}

        {showWeek && (
          <section className="flex flex-col">
            <div className="text-sm font-semibold text-white px-2 mb-2">
              {t('rail.thisWeek', 'This week')}
            </div>
            <RailStat label={t('rail.rendersFinished', 'Renders finished')} value={weekStats.renders} accent />
            <RailStat label={t('rail.draftsWaiting', 'Drafts waiting')} value={weekStats.drafts} />
            <RailStat
              label={t('rail.creditsLeft', 'Credits left')}
              value={weekStats.credits === null ? '—' : weekStats.credits.toLocaleString()}
            />
          </section>
        )}

        {trendingHubs.length > 0 && (
          <section className="flex flex-col">
            <div className="text-sm font-semibold text-white px-2 mb-2">
              {t('rail.trending', 'Trending')}
            </div>
            {trendingHubs.slice(0, 4).map((hub) => (
              <Link
                key={hub.id}
                to={`/h/${hub.id}`}
                className="px-2 py-2.5 rounded-lg hover:bg-white/4 transition-colors cursor-pointer"
              >
                <div className="text-[11px] uppercase tracking-wider text-[#9a9aac] mb-0.5">
                  {t('rail.hubTrending', 'Hub · Trending')}
                </div>
                {/* Hubs carry an emoji icon; showing it makes these cards
                    scannable instead of three lines of undifferentiated text. */}
                <div className="flex items-center gap-1.5 mb-0.5">
                  {hub.icon && <span aria-hidden="true" className="text-sm leading-none">{hub.icon}</span>}
                  <span dir="auto" className="text-sm font-semibold text-white">{hub.name}</span>
                </div>
                <div className="text-xs text-[#9a9aac]">
                  {hub.postCount24h} {t('rail.postsToday', 'posts today')}
                </div>
              </Link>
            ))}
          </section>
        )}

        {suggestions.length > 0 && (
          <section className="flex flex-col">
            <div className="text-sm font-semibold text-white px-2 mb-2">
              {t('rail.whoToFollow', 'Who to follow')}
            </div>
            {suggestions.slice(0, 4).map((s) => (
              <Link
                key={`${s.type}-${s.id}`}
                to={s.type === 'hub' ? `/h/${s.id}` : `/u/${s.username || s.id}`}
                className="px-2 py-2.5 rounded-lg hover:bg-white/4 transition-colors cursor-pointer flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-full bg-white/6 flex items-center justify-center text-sm font-semibold text-white overflow-hidden shrink-0">
                  {s.avatar
                    ? <img src={s.avatar} alt="" className="w-full h-full object-cover" />
                    : (s.name?.charAt(0)?.toUpperCase() || '?')}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white truncate">{s.name}</div>
                  <div className="text-xs text-[#9a9aac] truncate">
                    @{s.username || s.id.slice(0, 8)}
                  </div>
                </div>
              </Link>
            ))}
          </section>
        )}
        </div>
      </aside>
    );
  }

  // V2 — media / library pages own their own content full-width: the
  // storage widget + file grid already live on the page itself, so any
  // rail content would be either duplicative (storage stats) or stale
  // (trending hubs irrelevant to file management). Render nothing.
  if (designV2 && isMediaPage) {
    return null;
  }

  // Collections page sidebar
  if (isCollectionsPage) {
    return (
      <div className="w-80 shrink-0">
        <div className="sticky top-6 space-y-4">
          {/* Collections Overview */}
          <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-y-2">
              <div className="flex items-center gap-2">
                <FolderOpen className="w-5 h-5 text-iris-400" />
                <h3 className="font-semibold text-white">{t('sidebar.collections.title')}</h3>
              </div>
              <Link to="/dashboard/collections"
                className="text-xs text-brand-primary hover:underline"
              >
                {t('sidebar.collections.viewAll')}
              </Link>
            </div>

            <div className="space-y-2 mb-4">
              {collections.length > 0 ? (
                collections.map((col) => (
                  <Link
                    key={col.id}
                    to={`/dashboard/collections/${col.id}`}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#252542] transition-colors group"
                  >
                    <span className="text-xl">{col.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-white truncate group-hover:text-brand-primary transition-colors text-sm">
                        {col.name}
                      </div>
                      <div className="text-xs text-[#9a9aac] flex items-center gap-1">
                        {col.visibility === 'private' ? (
                          <Lock size={10} />
                        ) : (
                          <Globe size={10} />
                        )}
                        {col.itemCount} {t('sidebar.collections.items')}
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-[#9a9aac] mb-2">{t('sidebar.collections.noCollectionsYet')}</p>
                  <Link to="/dashboard/collections"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-iris-500/10 text-iris-300 hover:bg-iris-500/20 transition-colors"
                  >
                    <Plus size={12} />
                    {t('sidebar.collections.createYourFirst')}
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Collection Tips */}
          <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-amber-400" />
              <h3 className="font-semibold text-white">{t('sidebar.collections.tips')}</h3>
            </div>
            <ul className="space-y-2 text-sm text-[#a0a0b0]">
              <li className="flex items-start gap-2">
                <span className="text-iris-400 mt-0.5">•</span>
                <span>{t('sidebar.collections.tipPublic')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-iris-400 mt-0.5">•</span>
                <span>{t('sidebar.collections.tipAddItems')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-iris-400 mt-0.5">•</span>
                <span>{t('sidebar.collections.tipShareUnlisted')}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // Profile page sidebar
  if (isProfilePage) {
    return (
      <div className="w-80 shrink-0">
        <div className="sticky top-6 space-y-4">
          {/* Profile Card */}
          <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5 text-iris-400" />
              <h3 className="font-semibold text-white">{t('sidebar.profile.title')}</h3>
            </div>

            <div className="text-center mb-4">
              {profile?.avatarUrl ? (
                <img
                  src={profile.avatarUrl}
                  alt=""
                  className="w-16 h-16 rounded-full mx-auto mb-2 object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-full mx-auto mb-2 bg-linear-to-br/srgb from-iris-500 to-iris-500 flex items-center justify-center text-2xl text-[#0C0C12] font-bold">
                  {(profile?.displayName || profile?.username || 'U').charAt(0).toUpperCase()}
                </div>
              )}
              <h4 className="font-medium text-white">{profile?.displayName || t('sidebar.profile.yourName')}</h4>
              {profile?.username && (
                <p className="text-sm text-[#9a9aac]">@{profile.username}</p>
              )}
            </div>

            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-[#252542] rounded-lg p-2">
                <div className="text-lg font-bold text-white">{userStats?.followers || 0}</div>
                <div className="text-[12px] text-[#9a9aac]">{t('sidebar.profile.followers')}</div>
              </div>
              <div className="bg-[#252542] rounded-lg p-2">
                <div className="text-lg font-bold text-white">{collections.length}</div>
                <div className="text-[12px] text-[#9a9aac]">{t('sidebar.profile.collections')}</div>
              </div>
              <div className="bg-[#252542] rounded-lg p-2">
                <div className="text-lg font-bold text-white">{userStats?.totalPosts || 0}</div>
                <div className="text-[12px] text-[#9a9aac]">{t('sidebar.profile.posts')}</div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
            <h3 className="font-semibold text-white mb-3">{t('sidebar.profile.quickActions')}</h3>
            <div className="space-y-2">
              <Link to="/dashboard/settings"
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#252542] transition-colors text-[#a0a0b0] hover:text-white"
              >
                <Settings size={16} />
                <span className="text-sm">{t('sidebar.profile.editProfileSettings')}</span>
              </Link>
              {profile?.username && (
                <Link to={`/u/${profile.username}`}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#252542] transition-colors text-[#a0a0b0] hover:text-white"
                >
                  <Eye size={16} />
                  <span className="text-sm">{t('sidebar.profile.viewPublicProfile')}</span>
                </Link>
              )}
              <Link to="/dashboard/collections"
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#252542] transition-colors text-[#a0a0b0] hover:text-white"
              >
                <FolderOpen size={16} />
                <span className="text-sm">{t('sidebar.profile.manageCollections')}</span>
              </Link>
              <button
                onClick={() => {
                  if (profile?.username) {
                    navigator.clipboard.writeText(`${window.location.origin}/u/${profile.username}`);
                  }
                }}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#252542] transition-colors text-[#a0a0b0] hover:text-white w-full ltr:text-left rtl:text-right"
              >
                <Share2 size={16} />
                <span className="text-sm">{t('sidebar.profile.copyProfileLink')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Settings page sidebar
  if (isSettingsPage) {
    return (
      <div className="w-80 shrink-0">
        <div className="sticky top-6 space-y-4">
          {/* Account Overview */}
          <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="w-5 h-5 text-iris-400" />
              <h3 className="font-semibold text-white">{t('sidebar.settings.account')}</h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                {profile?.avatarUrl ? (
                  <img src={profile.avatarUrl} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-linear-to-br/srgb from-iris-500 to-iris-500 flex items-center justify-center text-[#0C0C12] font-bold">
                    {(profile?.displayName || 'U').charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <p className="font-medium text-white text-sm">{profile?.displayName || t('sidebar.settings.user')}</p>
                  {profile?.username && <p className="text-xs text-[#9a9aac]">@{profile.username}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Settings Sections */}
          <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
            <h3 className="font-semibold text-white mb-3">{t('sidebar.settings.quickLinks')}</h3>
            <div className="space-y-1">
              <Link to="/dashboard/settings" className="flex items-center gap-2 p-2 rounded-lg hover:bg-[#252542] text-sm text-[#a0a0b0] hover:text-white transition-colors">
                <User size={14} /> {t('sidebar.settings.profileSettings')}
              </Link>
              <Link to="/dashboard/settings/notifications" className="flex items-center gap-2 p-2 rounded-lg hover:bg-[#252542] text-sm text-[#a0a0b0] hover:text-white transition-colors">
                <MessageSquare size={14} /> {t('sidebar.settings.notificationPreferences')}
              </Link>
              <Link to="/dashboard/security" className="flex items-center gap-2 p-2 rounded-lg hover:bg-[#252542] text-sm text-[#a0a0b0] hover:text-white transition-colors">
                <Lock size={14} /> {t('sidebar.settings.securityAndPrivacy')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Analytics page sidebar
  if (isAnalyticsPage) {
    return (
      <div className="w-80 shrink-0">
        <div className="sticky top-6 space-y-4">
          {/* Quick Stats */}
          <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-emerald-400" />
              <h3 className="font-semibold text-white">{t('sidebar.analytics.quickStats')}</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#252542] rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-white">{formatNumber(userStats?.totalViews || 0)}</div>
                <div className="text-[12px] text-[#9a9aac]">{t('sidebar.analytics.totalViews')}</div>
              </div>
              <div className="bg-[#252542] rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-white">{userStats?.totalLikes || 0}</div>
                <div className="text-[12px] text-[#9a9aac]">{t('sidebar.analytics.totalLikes')}</div>
              </div>
              <div className="bg-[#252542] rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-white">{userStats?.followers || 0}</div>
                <div className="text-[12px] text-[#9a9aac]">{t('sidebar.analytics.followers')}</div>
              </div>
              <div className="bg-[#252542] rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-white">{userStats?.totalPosts || 0}</div>
                <div className="text-[12px] text-[#9a9aac]">{t('sidebar.analytics.posts')}</div>
              </div>
            </div>
          </div>

          {/* Analytics Tips */}
          <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-amber-400" />
              <h3 className="font-semibold text-white">{t('sidebar.analytics.growthTips')}</h3>
            </div>
            <ul className="space-y-2 text-sm text-[#a0a0b0]">
              <li className="flex items-start gap-2">
                <span className="text-emerald-400 mt-0.5">•</span>
                <span>{t('sidebar.analytics.tipPostConsistently')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-400 mt-0.5">•</span>
                <span>{t('sidebar.analytics.tipEngageTrending')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-400 mt-0.5">•</span>
                <span>{t('sidebar.analytics.tipShareBestContent')}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // Post Score page sidebar
  if (isScoopScorePage) {
    return (
      <div className="w-80 shrink-0">
        <div className="sticky top-6 space-y-4">
          {/* Score Card */}
          <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-4">
              <Trophy className="w-5 h-5 text-yellow-500" />
              <h3 className="font-semibold text-white">{t('sidebar.score.yourScore')}</h3>
            </div>
            <div className="text-center py-4">
              <div className="text-5xl font-bold text-yellow-500 mb-2">{userStats?.scoopScore || 0}</div>
              <p className="text-sm text-[#9a9aac]">{t('sidebar.score.postScore')}</p>
            </div>
          </div>

          {/* How to Improve */}
          <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
            <h3 className="font-semibold text-white mb-3">{t('sidebar.score.boostYourScore')}</h3>
            <ul className="space-y-2 text-sm text-[#a0a0b0]">
              <li className="flex items-start gap-2">
                <span className="text-yellow-400 mt-0.5">+5</span>
                <span>{t('sidebar.score.getLikes')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-400 mt-0.5">+10</span>
                <span>{t('sidebar.score.gainFollowers')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-400 mt-0.5">+15</span>
                <span>{t('sidebar.score.getReshared')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-400 mt-0.5">+20</span>
                <span>{t('sidebar.score.postOriginalContent')}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // Creator Fund page sidebar
  if (isCreatorFundPage || isCreatorPage) {
    return (
      <div className="w-80 shrink-0">
        <div className="sticky top-6 space-y-4">
          {/* Earnings Overview */}
          <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="w-5 h-5 text-emerald-400" />
              <h3 className="font-semibold text-white">{t('sidebar.creator.creatorProgram')}</h3>
            </div>
            <div className="text-center py-4">
              <p className="text-sm text-[#a0a0b0] mb-2">{t('sidebar.creator.yourPostScore')}</p>
              <div className="text-3xl font-bold text-yellow-500">{userStats?.scoopScore || 0}</div>
            </div>
          </div>

          {/* Eligibility */}
          <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
            <h3 className="font-semibold text-white mb-3">{t('sidebar.creator.eligibilityRequirements')}</h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full flex items-center justify-center ${(userStats?.scoopScore || 0) >= 100 ? 'bg-emerald-500' : 'bg-[#3e3e52]'}`}>
                  {(userStats?.scoopScore || 0) >= 100 && <span className="text-white text-[12px]">✓</span>}
                </div>
                <span className="text-[#a0a0b0]">{t('sidebar.creator.scoreRequirement')}</span>
              </li>
              <li className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full flex items-center justify-center ${(userStats?.followers || 0) >= 50 ? 'bg-emerald-500' : 'bg-[#3e3e52]'}`}>
                  {(userStats?.followers || 0) >= 50 && <span className="text-white text-[12px]">✓</span>}
                </div>
                <span className="text-[#a0a0b0]">{t('sidebar.creator.followersRequirement')}</span>
              </li>
              <li className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full flex items-center justify-center ${(userStats?.totalPosts || 0) >= 10 ? 'bg-emerald-500' : 'bg-[#3e3e52]'}`}>
                  {(userStats?.totalPosts || 0) >= 10 && <span className="text-white text-[12px]">✓</span>}
                </div>
                <span className="text-[#a0a0b0]">{t('sidebar.creator.postsRequirement')}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // My Posts page sidebar
  if (isMyPostsPage) {
    return (
      <div className="w-80 shrink-0">
        <div className="sticky top-6 space-y-4">
          {/* Post Stats */}
          <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-iris-400" />
              <h3 className="font-semibold text-white">{t('sidebar.myPosts.title')}</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#252542] rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-white">{userStats?.totalPosts || 0}</div>
                <div className="text-[12px] text-[#9a9aac]">{t('sidebar.myPosts.totalPosts')}</div>
              </div>
              <div className="bg-[#252542] rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-white">{userStats?.totalLikes || 0}</div>
                <div className="text-[12px] text-[#9a9aac]">{t('sidebar.myPosts.totalLikes')}</div>
              </div>
            </div>
          </div>

          {/* Post Tips */}
          <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-amber-400" />
              <h3 className="font-semibold text-white">{t('sidebar.myPosts.postingTips')}</h3>
            </div>
            <ul className="space-y-2 text-sm text-[#a0a0b0]">
              <li className="flex items-start gap-2">
                <span className="text-iris-400 mt-0.5">•</span>
                <span>{t('sidebar.myPosts.tipAddImages')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-iris-400 mt-0.5">•</span>
                <span>{t('sidebar.myPosts.tipUseTags')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-iris-400 mt-0.5">•</span>
                <span>{t('sidebar.myPosts.tipShareToHubs')}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // Hubs list page sidebar
  if (isHubsPage) {
    return (
      <div className="w-80 shrink-0">
        <div className="sticky top-6 space-y-4">
          {/* Your Hubs */}
          <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-y-2">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-iris-400" />
                <h3 className="font-semibold text-white">{t('sidebar.hubs.title')}</h3>
              </div>
              <Link to="/dashboard/scoops" className="text-xs text-brand-primary hover:underline">
                {t('sidebar.hubs.createHub')}
              </Link>
            </div>
            <p className="text-sm text-[#a0a0b0]">
              {t('sidebar.hubs.description')}
            </p>
          </div>

          {/* Trending Hubs */}
          {trendingHubs.length > 0 && (
            <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
                <h3 className="font-semibold text-white">{t('sidebar.hubs.trending')}</h3>
              </div>
              <div className="space-y-2">
                {trendingHubs.slice(0, 4).map((hub) => (
                  <Link
                    key={hub.id}
                    to={`/dashboard/scoops/${hub.id}`}
                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-[#252542] transition-colors group"
                  >
                    <Hash className="w-4 h-4 text-[#9a9aac]" />
                    <span className="text-sm text-[#a0a0b0] group-hover:text-white truncate">{hub.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Messages page sidebar
  // Messages page handles its own contextual details panel (members list
  // for groups, profile for 1:1) inside Messages.tsx — no generic sidebar
  // card here.
  if (isMessagesPage) {
    return null;
  }

  // Notifications page sidebar
  if (isNotificationsPage) {
    return (
      <div className="w-80 shrink-0">
        <div className="sticky top-6 space-y-4">
          <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="w-5 h-5 text-iris-400" />
              <h3 className="font-semibold text-white">{t('sidebar.notifications.title')}</h3>
            </div>
            <Link to="/dashboard/settings/notifications"
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-[#252542] text-sm text-[#a0a0b0] hover:text-white transition-colors"
            >
              <Settings size={14} />
              {t('sidebar.notifications.notificationSettings')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Rooms page sidebar
  if (isRoomsPage) {
    return (
      <div className="w-80 shrink-0">
        <div className="sticky top-6 space-y-4">
          <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="w-5 h-5 text-iris-400" />
              <h3 className="font-semibold text-white">{t('sidebar.rooms.title')}</h3>
            </div>
            <p className="text-sm text-[#a0a0b0]">
              {t('sidebar.rooms.description')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Security page sidebar
  if (isSecurityPage) {
    return (
      <div className="w-80 shrink-0">
        <div className="sticky top-6 space-y-4">
          <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="w-5 h-5 text-emerald-400" />
              <h3 className="font-semibold text-white">{t('sidebar.security.title')}</h3>
            </div>
            <ul className="space-y-2 text-sm text-[#a0a0b0]">
              <li className="flex items-start gap-2">
                <span className="text-emerald-400 mt-0.5">•</span>
                <span>{t('sidebar.security.tipStrongPassword')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-400 mt-0.5">•</span>
                <span>{t('sidebar.security.tipTwoFactor')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-400 mt-0.5">•</span>
                <span>{t('sidebar.security.tipReviewApps')}</span>
              </li>
            </ul>
          </div>
          <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
            <h3 className="font-semibold text-white mb-3">{t('sidebar.security.quickLinks')}</h3>
            <div className="space-y-1">
              <Link to="/dashboard/settings" className="flex items-center gap-2 p-2 rounded-lg hover:bg-[#252542] text-sm text-[#a0a0b0] hover:text-white transition-colors">
                <Settings size={14} /> {t('sidebar.security.accountSettings')}
              </Link>
              <Link to="/dashboard/settings/notifications" className="flex items-center gap-2 p-2 rounded-lg hover:bg-[#252542] text-sm text-[#a0a0b0] hover:text-white transition-colors">
                <MessageSquare size={14} /> {t('sidebar.security.notificationSettings')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Credits page sidebar
  if (isCreditsPage) {
    return (
      <div className="w-80 shrink-0">
        <div className="sticky top-6 space-y-4">
          <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-amber-400" />
              <h3 className="font-semibold text-white">{t('sidebar.credits.title')}</h3>
            </div>
            <p className="text-sm text-[#a0a0b0]">
              {t('sidebar.credits.description')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Invitations page sidebar
  if (isInvitationsPage) {
    return (
      <div className="w-80 shrink-0">
        <div className="sticky top-6 space-y-4">
          <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-4">
              <UserPlus className="w-5 h-5 text-iris-400" />
              <h3 className="font-semibold text-white">{t('sidebar.invitations.title')}</h3>
            </div>
            <p className="text-sm text-[#a0a0b0]">
              {t('sidebar.invitations.description')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // AI page sidebar
  if (isAIPage) {
    return (
      <div className="w-80 shrink-0">
        <div className="sticky top-6 space-y-4">
          <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-iris-400" />
              <h3 className="font-semibold text-white">{t('sidebar.ai.title')}</h3>
            </div>
            <p className="text-sm text-[#a0a0b0]">
              {t('sidebar.ai.description')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Discover page sidebar — trending hubs + suggested users, no "Your Stats"
  if (isDiscoverPage) {
    return (
      <div className="w-80 shrink-0">
        <div className="sticky top-6 space-y-4">
          {/* Trending Hubs */}
          {trendingHubs.length > 0 && (
            <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
                <h3 className="font-semibold text-white">{t('dashboard.trendingHubs') || 'Trending Hubs'}</h3>
              </div>
              <div className="space-y-2">
                {trendingHubs.slice(0, 5).map((hub) => (
                  <Link
                    key={hub.id}
                    to={`/dashboard/scoops/${hub.id}`}
                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-[#252542] transition-colors group"
                  >
                    <Hash className="w-4 h-4 text-[#9a9aac]" />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-[#a0a0b0] group-hover:text-white truncate block">{hub.name}</span>
                      <span className="text-xs text-[#9a9aac]">{hub.memberCount} {t('dashboard.members') || 'members'}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Suggested People */}
          {suggestions.length > 0 && (
            <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-iris-500" />
                <h3 className="font-semibold text-white">{t('dashboard.suggestedForYou') || 'Suggested for you'}</h3>
              </div>
              <div className="space-y-2">
                {suggestions.slice(0, 4).map((item) => (
                  <div key={item.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-[#252542] transition-colors">
                    <div className="w-8 h-8 rounded-full bg-linear-to-br/srgb from-iris-500 to-iris-500 flex items-center justify-center overflow-hidden">
                      {item.avatar ? (
                        <img src={item.avatar} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-white text-xs font-bold">{item.name.charAt(0).toUpperCase()}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <Link to={`/u/${item.username}`} className="text-sm text-[#a0a0b0] hover:text-white truncate block">{item.name}</Link>
                      <span className="text-xs text-[#9a9aac]">{item.followers} {t('dashboard.followers') || 'followers'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Bookmarks page sidebar
  if (isBookmarksPage) {
    return (
      <div className="w-80 shrink-0">
        <div className="sticky top-6 space-y-4">
          <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-4">
              <Heart className="w-5 h-5 text-rose-400" />
              <h3 className="font-semibold text-white">{t('sidebar.bookmarks.title')}</h3>
            </div>
            <p className="text-sm text-[#a0a0b0]">
              {t('sidebar.bookmarks.description')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Feed page sidebar
  if (isFeedPage) {
    return (
      <div className="w-80 shrink-0">
        <div className="sticky top-6 space-y-4">
          <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-iris-400" />
              <h3 className="font-semibold text-white">{t('sidebar.feed.title')}</h3>
            </div>
            <p className="text-sm text-[#a0a0b0]">
              {t('sidebar.feed.description')}
            </p>
          </div>
          {/* Suggested follows */}
          {suggestions.length > 0 && (
            <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
              <h3 className="font-semibold text-white mb-3">{t('sidebar.feed.suggested')}</h3>
              <div className="space-y-2">
                {suggestions.slice(0, 3).map((user) => (
                  <div key={user.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-[#252542] transition-colors">
                    <div className="w-8 h-8 rounded-full bg-linear-to-br/srgb from-iris-500 to-iris-500 flex items-center justify-center text-[#0C0C12] text-xs font-bold">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm text-[#a0a0b0]">{user.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Explore page sidebar
  if (isExplorePage) {
    return (
      <div className="w-80 shrink-0">
        <div className="sticky top-6 space-y-4">
          <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
              <h3 className="font-semibold text-white">{t('sidebar.explore.title')}</h3>
            </div>
            <p className="text-sm text-[#a0a0b0]">
              {t('sidebar.explore.description')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Reading List page sidebar
  if (isReadingListPage) {
    return (
      <div className="w-80 shrink-0">
        <div className="sticky top-6 space-y-4">
          <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-iris-400" />
              <h3 className="font-semibold text-white">{t('sidebar.readingList.title')}</h3>
            </div>
            <p className="text-sm text-[#a0a0b0]">
              {t('sidebar.readingList.description')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Only show default sidebar on home page — other pages either have their own sidebar or don't need one
  if (!isHomePage) return null;

  return (
    <div className="w-80 shrink-0">
      <div className="sticky top-6 space-y-4">
        {/* User Stats Card */}
        <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-5 h-5 text-yellow-500" />
            <h3 className="font-semibold text-white">{t('dashboard.yourStats') || 'Your Stats'}</h3>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Link to="/dashboard/scoop-score"
              className="bg-[#252542] rounded-lg p-3 hover:bg-[#2a2a4a] transition-colors"
            >
              <div className="text-2xl font-bold text-yellow-500">
                {userStats?.scoopScore || 0}
              </div>
              <div className="text-xs text-[#a0a0b0]">{t('dashboard.scoopScore') || 'Post Score'}</div>
            </Link>

            <Link to="/dashboard/profile"
              className="bg-[#252542] rounded-lg p-3 hover:bg-[#2a2a4a] transition-colors"
            >
              <div className="text-2xl font-bold text-white">
                {userStats?.followers || 0}
              </div>
              <div className="text-xs text-[#a0a0b0]">{t('dashboard.followers') || 'Followers'}</div>
            </Link>

            <div className="bg-[#252542] rounded-lg p-3">
              <div className="text-2xl font-bold text-white">
                {userStats?.totalPosts || 0}
              </div>
              <div className="text-xs text-[#a0a0b0]">{t('dashboard.posts') || 'Posts'}</div>
            </div>

            <div className="bg-[#252542] rounded-lg p-3">
              <div className="text-2xl font-bold text-white">
                {formatNumber(userStats?.totalViews || 0)}
              </div>
              <div className="text-xs text-[#a0a0b0]">{t('dashboard.views') || 'Views'}</div>
            </div>
          </div>
        </div>

        {/* Trending Hubs */}
        <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-y-2">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              <h3 className="font-semibold text-white">{t('dashboard.trendingHubs') || 'Trending Hubs'}</h3>
            </div>
            <Link to="/dashboard/discover"
              className="text-xs text-brand-primary hover:underline"
            >
              {t('dashboard.seeAll') || 'See all'}
            </Link>
          </div>

          {trendingHubs.length > 0 ? (
            <div className="space-y-3">
              {trendingHubs.map((hub) => (
                <Link
                  key={hub.id}
                  to={`/dashboard/scoops/${hub.id}`}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#252542] transition-colors group"
                >
                  <div className="w-10 h-10 rounded-lg bg-linear-to-br/srgb from-brand-primary/20 to-iris-500/20 flex items-center justify-center">
                    <Hash className="w-5 h-5 text-brand-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-white truncate group-hover:text-brand-primary transition-colors">
                      {hub.name}
                    </div>
                    <div className="text-xs text-[#9a9aac]">
                      {hub.memberCount} {t('dashboard.members') || 'members'} • {hub.postCount24h} {t('dashboard.postsToday') || 'posts today'}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-[#9a9aac] text-sm">
              {t('dashboard.noTrendingHubs') || 'No trending hubs yet'}
            </div>
          )}
        </div>

        {/* Suggested Hubs/People */}
        <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-y-2">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-iris-500" />
              <h3 className="font-semibold text-white">{t('dashboard.suggestedForYou') || 'Suggested for you'}</h3>
            </div>
          </div>

          {suggestions.length > 0 ? (
            <div className="space-y-3">
              {suggestions.map((item) => (
                <div
                  key={`${item.type}-${item.id}`}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#252542] transition-colors group"
                >
                  {item.type === 'hub' ? (
                    <>
                      <div className="w-10 h-10 rounded-lg bg-linear-to-br/srgb from-iris-500/20 to-pink-500/20 flex items-center justify-center">
                        <Hash className="w-5 h-5 text-iris-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <Link to={`/dashboard/scoops/${item.id}`}
                          className="font-medium text-white truncate group-hover:text-brand-primary transition-colors block"
                        >
                          {item.name}
                        </Link>
                        <div className="text-xs text-[#9a9aac] truncate">
                          {item.memberCount} {t('dashboard.members') || 'members'}
                        </div>
                      </div>
                      <button className="px-3 py-1.5 bg-brand-primary/10 text-brand-primary text-xs font-medium rounded-full hover:bg-brand-primary/20 transition-colors">
                        {t('dashboard.join') || 'Join'}
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="w-10 h-10 rounded-full bg-linear-to-br/srgb from-brand-primary/20 to-cyan-500/20 flex items-center justify-center overflow-hidden">
                        {item.avatar ? (
                          <img src={item.avatar} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Users className="w-5 h-5 text-brand-primary" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <Link to={`/u/${item.username}`}
                            className="font-medium text-white truncate group-hover:text-brand-primary transition-colors"
                          >
                            {item.name}
                          </Link>
                          {item.badge && isBadgeType(item.badge) && (
                            <BadgeIcon badgeType={item.badge} size="sm" />
                          )}
                        </div>
                        <div className="text-xs text-[#9a9aac] truncate">
                          {item.followers} {t('dashboard.followers') || 'followers'}
                        </div>
                      </div>
                      <button className="p-1.5 bg-brand-primary/10 text-brand-primary rounded-full hover:bg-brand-primary/20 transition-colors">
                        <UserPlus className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-[#9a9aac] text-sm">
              {t('dashboard.noSuggestions') || 'No suggestions yet'}
            </div>
          )}
        </div>

        {/* Activity Summary */}
        <div className="bg-[#16161e] rounded-xl p-4 border border-white/5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-y-2">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-cyan-500" />
              <h3 className="font-semibold text-white">{t('dashboard.todayActivity') || "Today's Activity"}</h3>
            </div>
            <Link to="/dashboard/analytics"
              className="text-xs text-brand-primary hover:underline flex items-center gap-1"
            >
              {t('dashboard.viewAnalytics') || 'View analytics'}
              <ChevronRight className="w-3 h-3" />
            </Link>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[#a0a0b0]">
                <Eye className="w-4 h-4" />
                <span className="text-sm">{t('dashboard.views') || 'Views'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-white">{activity?.viewsToday || 0}</span>
                <ChangeIndicator value={activity?.viewsChange || 0} />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[#a0a0b0]">
                <Heart className="w-4 h-4" />
                <span className="text-sm">{t('dashboard.likes') || 'Likes'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-white">{activity?.likesToday || 0}</span>
                <ChangeIndicator value={activity?.likesChange || 0} />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[#a0a0b0]">
                <MessageSquare className="w-4 h-4" />
                <span className="text-sm">{t('dashboard.comments') || 'Comments'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-white">{activity?.commentsToday || 0}</span>
                <ChangeIndicator value={activity?.commentsChange || 0} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChangeIndicator({ value }: { value: number }) {
  if (value === 0) return null;

  const isPositive = value > 0;
  return (
    <span className={`text-xs font-medium ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
      {isPositive ? '+' : ''}{value}%
    </span>
  );
}

function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
}

function RailStat({ label, value, accent }: { label: string; value: number | string; accent?: boolean }) {
  return (
    <div className="flex items-baseline justify-between px-2 py-1.5 border-b border-white/6 last:border-b-0 flex-wrap gap-y-2">
      <span className="text-[13px] text-[#9a9aac]">{label}</span>
      <span className={`font-mono text-[13px] tabular-nums ${accent ? 'text-brand-primary' : 'text-white'}`}>
        {value}
      </span>
    </div>
  );
}

export default RightSidebar;
